/**
 * Tur değerlendirme çubuğu — Reflexion döngüsünün giriş noktası.
 *
 * YALNIZCA kalıcı bir kütüphane personasının turlarında görünür. Wizard'ın
 * o soruya özel ürettiği geçici bir personayı beğenmenin bir karşılığı yok:
 * tartışma bitince o kimlik yok oluyor, ders yazacak bir yer kalmıyor. Oyu
 * gösterip hiçbir şey yapmamak, göstermemekten daha kötü olurdu.
 *
 * Etkileşim iki katmanlı:
 *   • Tek tık (👍/👎) — anında kaydeder, ek alan istemez. Asıl kullanım bu.
 *   • "Not ekle" — puan + boyut skorları + serbest metin. Model dersi
 *     bunlardan çıkarır; özellikle serbest metin en yüksek sinyaldir.
 *
 * Backend yeterli geri bildirim biriktiğinde konsolidasyonu KENDİ tetikler
 * ve sonucu yanıtta döner; burada onu bir bildirimle yüzeye çıkarıyoruz ki
 * kullanıcı öğrenmenin gerçekten olduğunu görsün.
 */
import { useState } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import type { FeedbackVerdict } from "../../domain/personaLibrary";
import {
  LibraryApiError,
  submitFeedback,
} from "../../transport/personaLibraryClient";

const DIMENSIONS: { key: string; label: string }[] = [
  { key: "evidence", label: "Kanıt" },
  { key: "clarity", label: "Anlaşılırlık" },
  { key: "originality", label: "Özgünlük" },
  { key: "relevance", label: "Uygunluk" },
  { key: "decisiveness", label: "Netlik" },
];

export function PersonaFeedbackBar({
  turnId,
  sessionId,
  speakerId,
  personaName,
  initialVerdict,
  onRecorded,
}: {
  turnId: string;
  sessionId: string;
  speakerId: string;
  personaName: string;
  initialVerdict?: FeedbackVerdict;
  onRecorded?: (verdict: FeedbackVerdict) => void;
}) {
  const [verdict, setVerdict] = useState<FeedbackVerdict>(initialVerdict ?? "neutral");
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const [dims, setDims] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send(next: FeedbackVerdict, withDetail: boolean) {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const res = await submitFeedback({
        turnId,
        sessionId,
        personaId: speakerId,
        verdict: next,
        ...(withDetail && rating !== null ? { rating } : {}),
        ...(withDetail && note.trim() ? { note: note.trim() } : {}),
        ...(withDetail && Object.keys(dims).length > 0 ? { dimensions: dims } : {}),
      });
      setVerdict(next);
      onRecorded?.(next);
      const learned = res.consolidation?.lessons ?? 0;
      setStatus(
        learned > 0
          ? `${personaName} bu geri bildirimlerden ${learned} yeni davranış kuralı çıkardı.`
          : "Kaydedildi.",
      );
      if (withDetail) {
        setExpanded(false);
        setNote("");
        setRating(null);
        setDims({});
      }
    } catch (err) {
      setError(
        err instanceof LibraryApiError ? err.message : "Geri bildirim kaydedilemedi.",
      );
    } finally {
      setBusy(false);
    }
  }

  const btn = (active: boolean, tone: "up" | "down") =>
    [
      "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] transition-colors",
      "disabled:opacity-40",
      active
        ? tone === "up"
          ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
          : "border-rose-500/50 bg-rose-500/15 text-rose-300"
        : "border-line-soft/60 bg-surface-2/40 text-fg-subtle hover:text-fg-strong",
    ].join(" ");

  return (
    <div className="flex flex-col gap-1.5 border-t border-line-soft/40 pt-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10.5px] uppercase tracking-wider text-fg-subtle">
          Bu katkı
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={() => void send(verdict === "like" ? "neutral" : "like", false)}
          className={btn(verdict === "like", "up")}
          aria-pressed={verdict === "like"}
          title={`${personaName} bu davranışı sürdürsün`}
        >
          <ThumbsUp className="h-3 w-3" aria-hidden />
          İsabetli
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void send(verdict === "dislike" ? "neutral" : "dislike", false)}
          className={btn(verdict === "dislike", "down")}
          aria-pressed={verdict === "dislike"}
          title={`${personaName} bu davranışı düzeltsin`}
        >
          <ThumbsDown className="h-3 w-3" aria-hidden />
          Zayıf
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="rounded-md px-1.5 py-1 text-[11px] text-fg-subtle transition-colors hover:text-fg-strong"
        >
          {expanded ? "Kapat" : "Not ekle"}
        </button>
        {status && <span className="text-[10.5px] text-emerald-400/80">{status}</span>}
        {error && <span className="text-[10.5px] text-rose-400">{error}</span>}
      </div>

      {expanded && (
        <div className="flex flex-col gap-2 rounded-lg border border-line-soft/50 bg-surface-2/30 p-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-fg-subtle">Genel puan</span>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(rating === n ? null : n)}
                className={[
                  "h-6 w-6 rounded-md border text-[11px] transition-colors",
                  rating === n
                    ? "border-accent-300/60 bg-[hsl(var(--accent)/0.2)] text-fg-strong"
                    : "border-line-soft/60 text-fg-subtle hover:text-fg-strong",
                ].join(" ")}
              >
                {n}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-1">
            {DIMENSIONS.map((d) => (
              <div key={d.key} className="flex items-center gap-2">
                <span className="w-20 shrink-0 text-[10.5px] text-fg-subtle">{d.label}</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() =>
                        setDims((prev) => {
                          const next = { ...prev };
                          if (next[d.key] === n) {
                            delete next[d.key];
                          } else {
                            next[d.key] = n;
                          }
                          return next;
                        })
                      }
                      className={[
                        "h-4 w-4 rounded-sm border transition-colors",
                        (dims[d.key] ?? 0) >= n
                          ? "border-accent-300/60 bg-[hsl(var(--accent)/0.45)]"
                          : "border-line-soft/60",
                      ].join(" ")}
                      aria-label={`${d.label} ${n}`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <textarea
            rows={2}
            value={note}
            maxLength={2000}
            placeholder="Ne iyi ya da eksikti? Somut yazarsan persona somut bir kural öğrenir."
            onChange={(e) => setNote(e.target.value)}
            className="w-full resize-y rounded-md border border-line-soft/60 bg-surface-0/50 px-2 py-1.5 text-[12px] text-fg-strong placeholder:text-fg-subtle focus:border-accent-300/50 focus:outline-none"
          />

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={busy || (verdict === "neutral" && !note.trim() && rating === null)}
              onClick={() => void send(verdict === "neutral" ? "like" : verdict, true)}
              className="rounded-md border border-accent-300/40 bg-[hsl(var(--accent)/0.18)] px-2.5 py-1 text-[11.5px] text-fg-strong transition-colors hover:bg-[hsl(var(--accent)/0.28)] disabled:opacity-40"
            >
              Değerlendirmeyi Gönder
            </button>
            <span className="text-[10.5px] text-fg-subtle">
              Yeterli geri bildirim birikince davranış kuralına dönüşür.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
